/**
 * Copyright © 2026 OwnersTable Inc. All rights reserved.
 * This source code is proprietary and confidential.
 * Unauthorized copying or distribution is strictly prohibited.
 */

import { styled, DialogTitle, TextField, Button, Typography } from "@mui/material";

export const CrossIconContainer = styled("div")({
  display: "flex",
  justifyContent: "flex-end",
  margin: "10px 10px 0px 0px",
  cursor: "pointer",
  "&:hover": {
    opacity: 0.7,
  },
});

export const StyledDialogTitle = styled(DialogTitle)({
  "&.MuiDialogTitle-root": {
    padding: "16px 24px",
  },
  fontWeight: "500",
  fontSize: "20px",
  lineHeight: "30px",
  letterSpacing: "0.35%",
  color: "#323232",
});

export const StyledFieldLabel = styled("label")({
  display: "block",
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  color: "#323232",
  marginBottom: "12px",
});

export const StyledReadOnlyField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    paddingLeft: "2px",
    paddingRight: "2px",
    "& fieldset": {
      borderColor: "#D8D9DB",
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "#D8D9DB",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
      "& .MuiOutlinedInput-input": {
        color: "#424242",
        WebkitTextFillColor: "#424242",
        cursor: "not-allowed",
      },
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#424242",
    fontSize: "16px",
    lineHeight: "24px",
    padding: "10px 12px",
  },
  "& .MuiInputBase-input::placeholder": {
    opacity: 0.6,
  },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, -9px) scale(0.75)",
    fontSize: "14px",
  },
});

export const createStyledReadOnlyFieldProps = () => ({
  InputLabelProps: {
    shrink: true,
  },
});

export const StyledEditField = styled(TextField)({
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#ffffff",
    borderRadius: "8px",
    paddingLeft: "2px",
    paddingRight: "2px",
    "& fieldset": {
      borderColor: "#D8D9DB",
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "#395bbd",
      borderWidth: "2px",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#1976d2",
      borderWidth: "2px",
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#323232",
    fontSize: "16px",
    lineHeight: "24px",
    padding: "10px 12px",
  },
  "& .MuiInputBase-input::placeholder": {
    color: "#9e9e9e",
    opacity: 1,
  },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, -9px) scale(0.75)",
    fontSize: "14px",
  },
});

const readOnlyAppearanceSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#f5f5f5",
    borderRadius: "8px",
    paddingLeft: "2px",
    paddingRight: "2px",
    "& fieldset": {
      borderColor: "#D8D9DB",
      borderRadius: "8px",
      borderWidth: "1px",
    },

    "&:hover fieldset": {
      borderColor: "#D8D9DB",
      borderWidth: "1px",
    },
    "&.Mui-disabled": {
      backgroundColor: "#f5f5f5",
      "& .MuiOutlinedInput-input": {
        color: "#424242",
        WebkitTextFillColor: "#424242",
        cursor: "not-allowed",
      },
    },
  },
  "& .MuiOutlinedInput-input": {
    color: "#424242",
    fontSize: "16px",
    lineHeight: "24px",
    padding: "10px 12px",
  },
  "& .MuiInputBase-input::placeholder": {
    opacity: 0.6,
  },
  "& .MuiInputLabel-root": {
    transform: "translate(14px, -9px) scale(0.75)",
    fontSize: "14px",
  },
};

export const createStyledEditFieldProps = (useReadOnlyAppearance = false) => ({
  InputLabelProps: {
    shrink: true,
  },
  ...(useReadOnlyAppearance ? { sx: readOnlyAppearanceSx } : {}),
});

export const FieldPair = styled("div")({
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: "16px",
  alignItems: "flex-start",
  marginBottom: "8px",
});

export const StyledButton = styled(Button)({
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 600,
  padding: "8px 24px",
  minWidth: "120px",
  borderRadius: "8px",
  boxShadow: "none",
  "&.MuiButton-contained": {
    backgroundColor: "#395bbd",
    color: "#ffffff",
    "&:hover": {
      backgroundColor: "#001b59",
      boxShadow: "none",
    },
    "&:disabled": {
      backgroundColor: "#001b59",
      opacity: 0.6,
    },
  },
  "&.MuiButton-outlined": {
    borderColor: "#395bbd",
    borderWidth: "2px",
    color: "#395bbd",
    "&:hover": {
      borderColor: "#001b59",
      borderWidth: "2px",
      color: "#001b59",
      backgroundColor: "transparent",
    },
    "&:disabled": {
      borderColor: "#D8D9DB",
      color: "#9e9e9e",
    },
  },
});

export const languageSelectorBox = {
  marginBottom: "16px",
  paddingBottom: "12px",
  borderBottom: "1px solid #e0e0e0",
};

export const scrollableContent = {
  paddingTop: "16px",
  paddingBottom: "16px",
};

export const fixedActionContainer = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: "12px",
  padding: "16px 24px",
  backgroundColor: "#ffffff",
  borderTop: "1px solid #e0e0e0",
};

export const StyledRestoreButton = styled(Button)({
  textTransform: "none",
  fontSize: "13px",
  fontWeight: 600,
  padding: "8px 24px",
  borderRadius: "8px",
  boxShadow: "none",
  borderColor: "#e95420",
  borderWidth: "2px",
  color: "#e95420",
  "&:hover": {
    borderColor: "#c7441c",
    borderWidth: "2px",
    backgroundColor: "rgba(233, 84, 32, 0.04)",
  },
  "&:disabled": {
    borderColor: "#D8D9DB",
    color: "#9e9e9e",
  },
});

export const StyledSubTitle = styled(Typography)({
  fontSize: "16px",
  fontWeight: 600,
  lineHeight: "24px",
  color: "#323232",
})
